import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiArrowLeft, FiScale, FiFileText, FiUserX, FiDollarSign, FiClock } = FiIcons;

const ExitProcesses = () => {
  const [selectedExit, setSelectedExit] = useState('dismissal');

  const dismissalTypes = {
    dismissal: {
      title: 'Dismissal Pathways',
      subtitle: 'Fair dismissal procedures by category',
      categories: [
        {
          type: 'Capability',
          icon: FiClock,
          color: 'bg-blue-600',
          process: [
            {
              stage: 'Performance Issues Identified',
              actions: [
                'Document specific performance concerns',
                'Set clear, measurable improvement targets',
                'Provide necessary training and support',
                'Regular review meetings with documented outcomes'
              ],
              timeline: '1-3 months',
              compliance: 'ACAS: Fair capability process requires support and reasonable time'
            },
            {
              stage: 'Formal Capability Process',
              actions: [
                '**First capability meeting** - set improvement plan',
                'Review period with regular monitoring',
                '**Final capability meeting** if no improvement',
                'Consider alternatives: redeployment, adjusted role',
                '**Dismissal decision** if capability cannot be achieved'
              ],
              timeline: '3-6 months',
              compliance: 'Legal: Must follow fair procedure, offer alternatives where possible'
            }
          ]
        },
        {
          type: 'Conduct (Misconduct)',
          icon: FiUserX,
          color: 'bg-red-600',
          process: [
            {
              stage: 'Investigation',
              actions: [
                'Suspend if necessary (with pay)',
                'Thorough investigation of allegations',
                'Gather evidence and witness statements',
                'Prepare investigation report'
              ],
              timeline: '1-3 weeks',
              compliance: 'Legal: Fair investigation required before disciplinary action'
            },
            {
              stage: 'Disciplinary Process',
              actions: [
                'Follow progressive discipline (warnings)',
                'Formal disciplinary hearings',
                'Right to be accompanied',
                'Consider all mitigation factors',
                '**Dismissal** for gross misconduct or after final warning'
              ],
              timeline: '2-6 weeks',
              compliance: 'Legal: Progressive discipline unless gross misconduct'
            }
          ]
        },
        {
          type: 'Redundancy',
          icon: FiDollarSign,
          color: 'bg-orange-600',
          process: [
            {
              stage: 'Business Need & Consultation',
              actions: [
                'Establish genuine redundancy situation',
                'Consider alternatives to redundancy',
                '**Individual consultation** (minimum 30 days for 20+ employees)',
                'Collective consultation if 20+ redundancies',
                'Notify ACAS if collective redundancies'
              ],
              timeline: '30-90 days',
              compliance: 'Legal: Statutory consultation periods, notification requirements'
            },
            {
              stage: 'Selection & Implementation',
              actions: [
                'Fair selection criteria applied consistently',
                'Offer suitable alternative employment',
                'Calculate statutory redundancy pay',
                'Issue redundancy notices',
                'Right of appeal against selection'
              ],
              timeline: '2-4 weeks',
              compliance: 'Legal: Fair selection, statutory payments, offer alternatives'
            }
          ]
        },
        {
          type: 'Other Substantial Reason (OSR)',
          icon: FiFileText,
          color: 'bg-purple-600',
          process: [
            {
              stage: 'Substantial Reason Assessment',
              actions: [
                'Identify the substantial reason (reorganization, breakdown in trust, etc.)',
                'Consider if reason justifies dismissal',
                'Explore alternatives to dismissal',
                'Consult with employee about changes'
              ],
              timeline: '2-4 weeks',
              compliance: 'Legal: Must be genuinely substantial reason, fair process required'
            },
            {
              stage: 'Fair Process',
              actions: [
                'Full consultation with employee',
                'Consider employee representations',
                'Explore compromise solutions',
                'Fair dismissal process if no alternative',
                'Right of appeal'
              ],
              timeline: '2-6 weeks',
              compliance: 'ACAS: Fair consultation, consideration of alternatives'
            }
          ]
        }
      ]
    },
    settlement: {
      title: 'Settlement Agreements',
      subtitle: 'Termination by mutual agreement',
      categories: [
        {
          type: 'Settlement Agreement Process',
          icon: FiScale,
          color: 'bg-green-600',
          process: [
            {
              stage: 'Initial Discussion',
              actions: [
                'Identify mutual benefit for settlement',
                'Initial without prejudice discussions',
                'Agree broad terms and timeline',
                'Employee seeks independent legal advice'
              ],
              timeline: '1-2 weeks',
              compliance: 'Legal: Must be genuinely voluntary, independent advice required'
            },
            {
              stage: 'Formal Agreement',
              actions: [
                'Draft settlement agreement with legal terms',
                'Include compensation, notice, references',
                'Employee receives independent legal advice (paid by employer)',
                '**7-day cooling off period** after signing',
                'Execute agreement and make payments'
              ],
              timeline: '2-3 weeks',
              compliance: 'Legal: Statutory requirements for valid settlement agreement'
            }
          ]
        }
      ]
    }
  };

  const currentExit = dismissalTypes[selectedExit];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/" className="mr-4">
          <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Exit Processes & Dismissal Procedures</h1>
          <p className="text-gray-600">Comprehensive termination workflows ensuring legal compliance</p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => setSelectedExit('dismissal')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedExit === 'dismissal' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Dismissal Pathways
          </button>
          <button
            onClick={() => setSelectedExit('settlement')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedExit === 'settlement' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Settlement Agreements
          </button>
          <Link
            to="/tribunal-prep"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Tribunal Preparation →
          </Link>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentExit.title}</h2>
        <p className="text-gray-600">{currentExit.subtitle}</p>
      </div>

      <div className="space-y-8">
        {currentExit.categories.map((category, categoryIndex) => (
          <motion.div
            key={categoryIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: categoryIndex * 0.1 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className={`${category.color} px-6 py-4`}>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={category.icon} className="text-white text-2xl" />
                <h3 className="text-white font-semibold text-xl">{category.type}</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {category.process.map((stage, stageIndex) => (
                  <div key={stageIndex} className="border-l-4 border-gray-200 pl-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 text-lg">{stage.stage}</h4>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded">
                        {stage.timeline}
                      </span>
                    </div>

                    <ul className="space-y-2 mb-4">
                      {stage.actions.map((action, actionIndex) => (
                        <li key={actionIndex} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                          <span 
                            className="text-gray-700"
                            dangerouslySetInnerHTML={{ 
                              __html: action.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                            }}
                          />
                        </li>
                      ))}
                    </ul>

                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Compliance:</strong> {stage.compliance}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h4 className="font-semibold text-red-900 mb-3">⚖️ Legal Requirements</h4>
          <ul className="text-sm text-red-800 space-y-2">
            <li>• Minimum statutory notice periods</li>
            <li>• Fair dismissal procedures</li>
            <li>• Right of appeal</li>
            <li>• Statutory redundancy payments</li>
            <li>• ACAS Early Conciliation before tribunal</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="font-semibold text-green-900 mb-3">🟢 ACAS Best Practices</h4>
          <ul className="text-sm text-green-800 space-y-2">
            <li>• Early intervention and support</li>
            <li>• Fair and consistent application</li>
            <li>• Reasonable adjustments considered</li>
            <li>• Proper documentation throughout</li>
            <li>• Alternative solutions explored</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ExitProcesses;